import {
  memo,
  FunctionComponent,
  SyntheticEvent,
  Dispatch,
  useCallback,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Game as GameType } from '~types';
import { Button, Price } from '~ui/index';
import { GetName, SteamItemImage } from '../item';
import GameChange from '../game/GameChange';
import Currency from '../Currency';
import { useConfig } from '~components/hooks';

interface WinGameProps {
  winGame: GameType;
  setWinGame: Dispatch<any>;
  setAnimateClass: Dispatch<any>;
}

export const WinGame: FunctionComponent<WinGameProps> = memo(
  ({ winGame, setWinGame, setAnimateClass }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const getConfig = useConfig();

    const onRefresh = useCallback(
      (event: SyntheticEvent) => {
        if (event) {
          event.preventDefault();
        }

        setWinGame(null);
        setAnimateClass(null);
      },
      [setAnimateClass, setWinGame],
    );

    const addContracts = useCallback(
      (event: SyntheticEvent) => {
        if (event) {
          event.preventDefault();
        }

        setWinGame(null);
        setAnimateClass(null);

        navigate('/contract', {
          state: { gameIds: [winGame.id] },
        });
      },
      [navigate, setAnimateClass, setWinGame, winGame.id],
    );

    return (
      <div className="contracts-slots-wrap">
        <div className="contracts-slots-win">
          <div className="contracts-slots-win-item">
            <SteamItemImage url={winGame.getItem.getImage} size="350x350" />
            <div className="contracts-slots-win-name">
              <span className="name">
                <GetName getItem={winGame.getItem} type={0} />{' '}
              </span>
              <span>
                <GetName getItem={winGame.getItem} type={1} />
              </span>
            </div>
            <div className="contracts-slots-win-other">
              {winGame.getItem.isStatTrak && (
                <div className="stattrack">
                  <span className="fas fa-star" /> StatTrak™
                </div>
              )}
              {/* <span className="drop-chance">{winGame?.chance}%</span> */}
            </div>
          </div>
          <div className="case-play-win-btns">
            <a
              className="btn-game btn-game--repeat"
              href="#"
              onClick={onRefresh}
            >
              <span className="icon material-icons" data-icon="sync" />
              <span className="text">{t('Play again')}</span>
            </a>
            <GameChange
              getGame={winGame}
              type={0}
              onDone={onRefresh}
              className="btn btn-sell"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="42"
                height="42"
                viewBox="0 0 42 42"
              >
                <path
                  d="M1992,1385.667l16-16h-8V1367h13.333v13.333h-2.667v-8l-16,16Z"
                  transform="translate(-1981.333 -1356.333)"
                  fill="#fff"
                ></path>
              </svg>
              <span className="text">{t('Sell for')}</span>
              <div className="cost">
                {getConfig.isEU ? (
                  <>
                    <Currency />
                    <Price sum={winGame?.price} />
                  </>
                ) : (
                  <>
                    <Price sum={winGame?.price} />
                    <Currency />
                  </>
                )}
              </div>
            </GameChange>
            <Button
              className="btn-game btn-game--contracts"
              onClick={addContracts}
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21.25 35H18.75V32.5C15.8336 32.2885 13.087 31.0484 11 29.0002C8.95169 26.9134 7.71128 24.1665 7.5 21.25H5V18.75H7.5C7.71128 15.8335 8.95169 13.0866 11 10.9998C13.0873 8.95199 15.8337 7.71192 18.75 7.5V5H21.25V7.5C24.1663 7.71203 26.9127 8.95208 29 10.9998C31.0484 13.0866 32.2888 15.8335 32.5 18.75H35V21.25H32.5C32.2884 24.1664 31.048 26.9132 29 29.0002C26.9127 31.0479 24.1663 32.288 21.25 32.5V35ZM21.25 28.75V30C23.5215 29.8506 25.6614 28.8808 27.2711 27.2711C28.8808 25.6614 29.8506 23.5215 30 21.25H28.75V18.75H30C29.8503 16.4786 28.8804 14.3391 27.2708 12.7295C25.6611 11.1199 23.5215 10.1497 21.25 10V11.25H18.75V10C16.4783 10.1489 14.3381 11.1185 12.7283 12.7283C11.1186 14.3381 10.1489 16.4783 10 18.75H11.25V21.25H10C10.1471 23.5224 11.1161 25.6634 12.7263 27.2736C14.3365 28.8837 16.4776 29.8529 18.75 30V28.75H21.25ZM20 26.25C18.3621 26.1881 16.8081 25.5095 15.6492 24.3506C14.4902 23.1916 13.8119 21.6378 13.75 20C13.8119 18.3621 14.4902 16.8084 15.6492 15.6494C16.8081 14.4905 18.3621 13.8119 20 13.75C21.6378 13.8119 23.1919 14.4905 24.3508 15.6494C25.5098 16.8084 26.1881 18.3621 26.25 20C26.1881 21.6378 25.5098 23.1916 24.3508 24.3506C23.1919 25.5095 21.6378 26.1881 20 26.25ZM20 16.25C19.0054 16.25 18.0516 16.6454 17.3483 17.3486C16.6451 18.0519 16.25 19.0054 16.25 20C16.25 20.9946 16.6451 21.9481 17.3483 22.6514C18.0516 23.3546 19.0054 23.75 20 23.75C20.9946 23.75 21.9484 23.3546 22.6516 22.6514C23.3549 21.9481 23.75 20.9946 23.75 20C23.75 19.0054 23.3549 18.0519 22.6516 17.3486C21.9484 16.6454 20.9946 16.25 20 16.25Z"
                  fill="white"
                ></path>
              </svg>
              <span className="text">{t('add_to_contract')}</span>
            </Button>
          </div>
        </div>
      </div>
    );
  },
);
